import { get, merge } from 'lodash';
import { config as logRocketConfig } from 'modules/taboola-common-frontend-modules/log-rocket/config';
import injectSnippet from './injectSnippet';

class IntercomService {
    constructor() {
        this.isBooted = false;
        this.isOpen = false;
        this.toggle.bind(this);
        this.hide.bind(this);
        this.eventListeners = {};
    }

    async boot(intercomBootParameters) {
        if (this.isBooted) {
            return;
        }

        window.intercomSettings = intercomBootParameters;
        window.Intercom('boot', intercomBootParameters);
        this.isBooted = true;

        const sessionURL = await logRocketConfig.getSessionUrlAsync();
        window.Intercom('trackEvent', 'LogRocket', { sessionURL });
        this.onShow(() => (this.isOpen = true));
        this.onHide(() => (this.isOpen = false));
    }

    isServiceBooted() {
        return this.isBooted;
    }

    initIntercomService(bootParameters, updateParameters) {
        if (!window.Intercom && bootParameters) {
            injectSnippet(bootParameters);
        }
        if (window.Intercom) {
            this.boot(bootParameters);
            this.update(updateParameters);
        }
    }

    shutdown() {
        if (!this.isBooted) {
            return;
        }

        window.Intercom('shutdown');
        this.isBooted = false;
    }

    update(intercomParameters) {
        if (!this.isBooted) {
            return;
        }

        const logRocketParams = { logrocketURL: logRocketConfig.getSessionsUrl() };

        if (!intercomParameters) {
            const lastRequestedAt = new Date().getTime() / 1000;
            window.Intercom('update', { last_requested_at: lastRequestedAt, ...logRocketParams });
            return;
        }

        window.intercomSettings = merge({ ...logRocketParams }, window.intercomSettings, intercomParameters);
        window.Intercom('update', intercomParameters);
    }

    hide() {
        if (!this.isBooted) {
            return;
        }

        window.Intercom('hide');
    }

    show() {
        if (!this.isBooted) {
            return;
        }

        window.Intercom('show');
    }

    toggle() {
        if (this.isOpen) {
            this.isOpen = false;
            this.hide();
            return;
        }
        this.isOpen = true;
        this.show();
    }

    addEventListener(eventName, listener) {
        if (!this.isBooted) {
            return;
        }

        const shouldIntercomSubscribe = !this.eventListeners[eventName];
        const prevListeners = get(this.eventListeners, eventName, []);

        this.eventListeners[eventName] = [...prevListeners, listener];

        const unlisten = () => {
            this.eventListeners[eventName] = this.eventListeners[eventName].filter(
                currentListener => currentListener !== listener
            );
        };

        if (shouldIntercomSubscribe) {
            window.Intercom(eventName, (...args) => {
                get(this.eventListeners, eventName, []).forEach(singleListener => singleListener(...args));
            });
        }

        return unlisten;
    }

    onHide(callback) {
        return this.addEventListener('onHide', callback);
    }

    onShow(callback) {
        return this.addEventListener('onShow', callback);
    }

    onUnreadCountChange(callback) {
        return this.addEventListener('onUnreadCountChange', callback);
    }

    hideIcon() {
        if (!this.isBooted) {
            return;
        }
        window.intercomSettings = { ...window.intercomSettings, hide_default_launcher: true };
        window.Intercom('update', {
            hide_default_launcher: true,
        });
    }

    showIcon() {
        if (!this.isBooted) {
            return;
        }
        window.intercomSettings = { ...window.intercomSettings, hide_default_launcher: false };
        window.Intercom('update', {
            hide_default_launcher: false,
        });
    }

    getIconHidden() {
        return window?.intercomSettings?.hide_default_launcher;
    }

    getIsOpen() {
        return this.isOpen;
    }
}

export const intercomService = new IntercomService();
